import React from 'react';
import { faPencilAlt, faSignInAlt } from '@fortawesome/free-solid-svg-icons';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { RowInfo } from 'react-table';
import { CreateUserViewModel, UserRole, UserViewModel } from 'stageclip-common';

import { ClientsUserFormModal } from '../form/ClientsUserFormModal';

import { DeleteUserModal } from 'components/deleteUserModal/DeleteUserModal';
import * as PermissionsHelper from 'helpers/permissionsHelper';
import { ClippingPlatformState } from 'store/clippingPlatformState';
import Button from 'components/buttons/button';
import { ModalContainer } from 'components/guardedModal/ModalContainer';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from 'components/componentBase';
import { AppTable, AppTableColumn } from 'components/table/appTable';
import { fetchUsersRequest } from 'api/clients/clients.actions';
import { createModalOpenAction } from 'actions/modalActions';
import { FormId } from 'constants/formId';
import { ClientsActionTypes } from 'api/clients/clients.action-types';

interface ClientsUsersListComponentStateProps extends BaseStateProps {
  users: CreateUserViewModel[];
  sessionToken: string;
}

interface ClientsUsersListComponentDispatchProps extends BaseDispatchProps {
  fetchUsers: () => void;
  openEditForm: (data: UserViewModel) => void;
  impersonateUser: (userId: string, token?: string) => Promise<string>;
}

class ClientsUsersListComponent extends ComponentBase<
  RouteComponentProps,
  ClientsUsersListComponentStateProps,
  ClientsUsersListComponentDispatchProps
> {
  state = {
    loaded: false,
  };

  private columns: AppTableColumn[] = [
    {
      Header: 'First name',
      accessor: (user: CreateUserViewModel) => user.firstName,
      id: 'firstName',
    },
    {
      Header: 'Last name',
      accessor: (user: CreateUserViewModel) => user.lastName,
      id: 'lastName',
    },
    {
      Header: 'email',
      accessor: (user: CreateUserViewModel) => user.email,
      id: 'email',
    },
    {
      Cell: (row: RowInfo) => {
        const { openEditForm } = this.props;

        return (
          <>
            {PermissionsHelper.userCan(PermissionsHelper.RestrictedActions.EditClient) && (
              <Button
                text="Edit User"
                onClick={() => openEditForm(row.original)}
                icon={faPencilAlt}
                allCapitals
                slimButton
              />
            )}
          </>
        );
      },
      Header: 'Actions',
      id: 'actions',
    },
    {
      Cell: (row: any) => this.renderLoginAsButton(row.original),
      Header: 'Login as',
      accessor: (user: UserViewModel) => user,
      id: 'loginAs',
      width: 150,
      show: this.props.currentUser.role === UserRole.Admin,
    },
  ];

  protected async loadData(props: ClientsUsersListComponentDispatchProps) {
    await props.fetchUsers();
    this.setState({ loaded: true });
  }

  protected dataLoaded() {
    return this.state.loaded;
  }

  protected renderLoginAsButton(userData: UserViewModel) {
    if (userData.role !== UserRole.User) return null;

    return <Button text="Login As" icon={faSignInAlt} slimButton onClick={this.loginAs(userData.id)} />;
  }

  protected loginAs = (userId: string) => async () => {
    const userToken = await this.props.impersonateUser(userId);
    const url = `${process.env.REACT_APP_CLIENT_PORTAL_URL}/login?token=${userToken}`;
    window.open(url, '_blank');
  };

  protected loggedInRender() {
    return (
      <>
        <AppTable
          columns={this.columns}
          data={this.props.users}
          tableName="clients"
          defaultSorted={[{ id: 'firstName', desc: false }]}
        />
        <ModalContainer id={FormId.ClientsUserEdit}>
          <ClientsUserFormModal
            formTitle="Edit user"
            submitActionType={ClientsActionTypes.EDIT_CLIENT_USER_REQUEST}
            formId={FormId.ClientsUserEdit}
          />
        </ModalContainer>
        <DeleteUserModal />
      </>
    );
  }
}

const usersSelector = (state: ClippingPlatformState) => state.entities.clientsUsers;

const getAllClients = (state: ClippingPlatformState) => {
  const { allIds, byId } = usersSelector(state);

  return allIds.map(id => byId[id]);
};

const mapStateToProps = (state: ClippingPlatformState): ClientsUsersListComponentStateProps => ({
  ...baseMapStateToProps(state),
  sessionToken: state.appStatus.session.sessionToken,
  users: getAllClients(state),
});

const mapDispatchToProps = (
  dispatch: Dispatch,
  ownProps: RouteComponentProps<{ id: string }>,
): ClientsUsersListComponentDispatchProps => {
  const { id } = ownProps.match.params;

  return {
    ...baseMapDispatchToProps(dispatch),
    impersonateUser: async (userId: string, token: string) => {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/sessions/impersonate`, {
        method: 'post',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId }),
      }).then(res => res.json());

      return response.sessionToken;
    },
    fetchUsers: () => dispatch(fetchUsersRequest(id)),
    openEditForm: data =>
      dispatch(
        createModalOpenAction(FormId.ClientsUserEdit, {
          ...data,
          clientId: id,
        }),
      ),
  };
};

const mergeProps = (
  stateProps: ClientsUsersListComponentStateProps,
  dispatchProps: ClientsUsersListComponentDispatchProps,
  ownProps: RouteComponentProps<{ id: string }>,
) => ({
  ...ownProps,
  ...stateProps,
  ...dispatchProps,
  impersonateUser: (userId: string) => dispatchProps.impersonateUser(userId, stateProps.sessionToken),
});

export const ClientsUsersList = withRouter(
  connect(mapStateToProps, mapDispatchToProps, mergeProps)(ClientsUsersListComponent),
);
